import { Fragment, useMemo } from "react";
import type { CompanyShellRow } from "../../generated/core-api";
import {
  cellErrorKey,
  CSV_HEADER_TO_FIELD_PATH,
  formatCompanyShellRowForPreview,
  unknownShellCsvColumnCellKey,
} from "../hooks";
import type { CompanyShellCsvColumn, CompanyShellNormalisation } from "../types";
import { companyCellEditKey } from "./companyCellEditKey";
import { InlineEditableField } from "./InlineEditableField";
import { inlineCompanyFieldIssues } from "./inlineCompanyFieldIssues";
import s from "./CompanyRowsTable.module.css";

/** Columns pinned on the left as the row identity; the rest expand downward. */
const DEFAULT_PINNED_HEADERS = ["Company Code", "Company Name"];

/** CSV header -> an HTML-id-safe fragment, for wiring `aria-describedby`. */
function headerToIdFragment(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export interface CompanyRowsTableProps {
  /** CSV columns in display order (header + requiredness legend). */
  columns: CompanyShellCsvColumn[];
  /** One company per row. */
  rows: CompanyShellRow[];
  /** Headers kept as fixed left identity columns; everything else lists downward. */
  pinnedHeaders?: string[];
  /** When true, identity + value cells render editable inputs. */
  editable?: boolean;
  /** Called on every cell edit with the row's array index, the CSV header, and the new value. */
  onCellChange?: (rowArrayIndex: number, header: string, value: string) => void;
  /**
   * 0-based `row_index` → field-path-keyed reasons (see `cellErrorKey`,
   * field path from `CSV_HEADER_TO_FIELD_PATH`). Anchors a Fail/Pass to the
   * specific CSV column/value line it's about, rather than the row overall.
   */
  cellErrors?: Map<string, string[]>;
  /**
   * 0-based `row_index` → reason messages (column-level and row-level
   * combined). Drives whether the whole company is highlighted as failed,
   * and — for any message that isn't shown on a specific column's line
   * (a genuine row-level error, or a column `mapValidationErrorsToCells`
   * couldn't match to a CSV header) — a pinned top line for that row.
   * Also gates whether the Checks column renders at all: omit entirely
   * before the first validate run.
   */
  errorsByRow?: Map<number, string[]>;
  /** Cells saved during this review session. */
  editedCellKeys?: ReadonlySet<string>;
  /**
   * Values the backend normalised for display (@AC-UPLOAD-15,
   * Pacific.Agentic.Api#954) — each entry's `column` is a field path anchored
   * the same way as `cellErrors` (via `CSV_HEADER_TO_FIELD_PATH`). When a
   * normalisation exists for a rendered column's cell, its canonical value is
   * shown as a hint beneath the raw value (e.g. a reformatted IRD number).
   */
  normalisations?: CompanyShellNormalisation[];
  /** Accessible label for the scroll region. */
  ariaLabel?: string;
}

function CheckBadge({
  reasons,
  visibleReasons = reasons,
  reasonsId,
}: {
  reasons: string[];
  visibleReasons?: string[];
  reasonsId?: string;
}) {
  const passed = reasons.length === 0;
  return (
    <div className={s.checkInner}>
      {/*
       * `role="status"` (implicit `aria-live="polite"`) so a re-validate that
       * flips this cell Pass<->Fail is announced to screen-reader users, not
       * just the page-level banners. The visible "Pass"/"Fail" text is the
       * accessible name — no `aria-label`, which would only double it up
       * ("Failed, Fail").
       */}
      <span className={passed ? s.badgePass : s.badgeFail} role="status">
        {passed ? "Pass" : "Fail"}
      </span>
      {!passed && visibleReasons.length > 0 && (
        <ul id={reasonsId} className={s.reasons}>
          {visibleReasons.map((message, k) => (
            <li key={k} className={s.reason}>
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One rendered sub-row: either a real CSV column, or the pinned row-level line. */
type Line =
  | { kind: "row-level"; reasons: string[] }
  | { kind: "column"; column: CompanyShellCsvColumn | undefined; reasons: string[] };

/**
 * The bulk-company rows rendered with the two identity columns
 * (Company Code / Company Name) pinned on the left via `rowSpan`, and every
 * other column expanding vertically as `CSV column | Value` sub-rows — so a
 * ~40-column shell reads top-to-bottom instead of scrolling sideways. Shared
 * by read-only summaries and the combined Upload & Review screen.
 *
 * When `errorsByRow` is given, each column's line gets its own Pass/Fail —
 * a `cellErrors` reason anchors to the specific field it's about, right
 * beside its value, rather than one combined badge for the whole company.
 * A message that isn't tied to a rendered column (a genuine row-level
 * error, or one `cellErrors` couldn't match to a CSV header) is pinned to
 * an extra "Other issues" line at the top instead of being dropped.
 */
export function CompanyRowsTable({
  columns,
  rows,
  pinnedHeaders = DEFAULT_PINNED_HEADERS,
  editable = false,
  onCellChange,
  cellErrors,
  errorsByRow,
  editedCellKeys,
  normalisations,
  ariaLabel = "Company rows",
}: CompanyRowsTableProps) {
  const pinnedColumns = pinnedHeaders
    .map((header) => columns.find((c) => c.header === header))
    .filter((c): c is CompanyShellCsvColumn => c !== undefined);
  const verticalColumns = columns.filter(
    (c) => !pinnedHeaders.includes(c.header)
  );
  const showChecks = errorsByRow !== undefined;

  // Normalised values keyed the same way as `cellErrors` — `${row_index}:
  // ${fieldPath}` — so a column line can look its own normalisation up by the
  // field path `CSV_HEADER_TO_FIELD_PATH` maps its header to. Last-wins on the
  // (unexpected) chance the backend sends two for the same cell.
  const normalisationsByCell = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of normalisations ?? []) {
      map.set(cellErrorKey(n.row_index, n.column), n.normalised_value);
    }
    return map;
  }, [normalisations]);

  const renderValue = (
    rowArrayIndex: number,
    column: CompanyShellCsvColumn,
    value: string,
    reasons: string[],
    describedById?: string
  ) => {
    if (!editable) return value || "—";
    return (
      <InlineEditableField
        label={column.header}
        value={value}
        isInvalid={reasons.length > 0}
        isEdited={
          editedCellKeys?.has(
            companyCellEditKey(rowArrayIndex, column.header)
          ) ?? false
        }
        isRequired={column.requiredness === "required"}
        describedBy={describedById}
        onCommit={(next) =>
          onCellChange?.(rowArrayIndex, column.header, next)
        }
      />
    );
  };

  return (
    <div
      className={s.tableWrapper}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <table className={s.table}>
        <thead>
          <tr>
            {pinnedColumns.map((column) => (
              <th key={column.header} scope="col" className={s.th}>
                {column.header}
              </th>
            ))}
            <th scope="col" className={s.th}>
              CSV column
            </th>
            <th scope="col" className={s.th}>
              Value
            </th>
            {showChecks && (
              <th scope="col" className={s.th}>
                Checks
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const display = formatCompanyShellRowForPreview(row);
            const allReasons = errorsByRow?.get(i) ?? [];
            const rowKey = row.company_code || String(i);
            // Guard the (unrealistic) no-vertical-columns case so identity
            // cells always render at least one row.
            const subColumns =
              verticalColumns.length > 0 ? verticalColumns : [undefined];

            const reasonsForColumn = (
              column: CompanyShellCsvColumn | undefined
            ): string[] => {
              if (!column) return [];
              // Prefer the validate field path when the header is a known
              // template column; otherwise use the namespaced undeclared-header
              // key so ExtraField (etc.) can Fail on its own grid line without
              // colliding with a backend field path of the same name.
              const fieldPath = Object.prototype.hasOwnProperty.call(
                CSV_HEADER_TO_FIELD_PATH,
                column.header
              )
                ? CSV_HEADER_TO_FIELD_PATH[column.header]
                : unknownShellCsvColumnCellKey(column.header);
              return cellErrors?.get(cellErrorKey(i, fieldPath)) ?? [];
            };
            const identityReasons = pinnedColumns.map(reasonsForColumn);
            const columnReasons = subColumns.map(reasonsForColumn);
            // Anything in errorsByRow not already shown on a column's line —
            // a genuine row-level error, or a column errors couldn't be
            // matched to a rendered CSV header — is pinned to a top line
            // instead of being silently dropped. Dedup by *count*, not mere
            // text presence: if a row-level error happens to share exact text
            // with an unrelated column error on the same row, only the copy
            // actually rendered inline is subtracted, so the other still
            // surfaces under "Other issues" rather than vanishing from both.
            const inlineCounts = new Map<string, number>();
            for (const message of [
              ...identityReasons.flat(),
              ...columnReasons.flat(),
            ]) {
              inlineCounts.set(message, (inlineCounts.get(message) ?? 0) + 1);
            }
            const rowLevelReasons = allReasons.filter((message) => {
              const remaining = inlineCounts.get(message) ?? 0;
              if (remaining > 0) {
                inlineCounts.set(message, remaining - 1);
                return false;
              }
              return true;
            });

            const lines: Line[] =
              showChecks && rowLevelReasons.length > 0
                ? [{ kind: "row-level", reasons: rowLevelReasons }]
                : [];
            subColumns.forEach((column, j) => {
              lines.push({ kind: "column", column, reasons: columnReasons[j] });
            });
            const span = lines.length;

            const identityCells = (
              <>
                {pinnedColumns.map((column, columnIndex) => {
                  const reasons = identityReasons[columnIndex];
                  const visibleReasons = inlineCompanyFieldIssues(
                    column.header,
                    reasons
                  );
                  const reasonsId =
                    visibleReasons.length > 0
                      ? `${rowKey}-${headerToIdFragment(column.header)}-checks`
                      : undefined;
                  return (
                    <td
                      key={column.header}
                      className={s.identityCell}
                      rowSpan={span}
                    >
                      {renderValue(
                        i,
                        column,
                        display[column.header] ?? "",
                        reasons,
                        reasonsId
                      )}
                      {visibleReasons.length > 0 && (
                        <ul id={reasonsId} className={s.identityReasons}>
                          {visibleReasons.map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  );
                })}
              </>
            );

            return (
              <Fragment key={rowKey}>
                {lines.map((line, j) => {
                  const visibleReasons =
                    line.kind === "column" && line.column
                      ? inlineCompanyFieldIssues(
                          line.column.header,
                          line.reasons
                        )
                      : line.reasons;
                  const reasonsId =
                    line.kind === "column" &&
                    line.column &&
                    visibleReasons.length > 0
                      ? `${rowKey}-${headerToIdFragment(line.column.header)}-checks`
                      : undefined;
                  return (
                    <tr
                      key={
                        line.kind === "row-level"
                          ? "row-level"
                          : line.column
                            ? line.column.header
                            : "empty"
                      }
                      className={
                        line.reasons.length > 0 ? s.failRow : undefined
                      }
                    >
                      {j === 0 && identityCells}
                      {line.kind === "row-level" ? (
                        <>
                          <td className={s.csvColumnCell}>
                            <div className={s.csvColumnName}>Other issues</div>
                          </td>
                          <td className={s.valueCell}>—</td>
                        </>
                      ) : line.column ? (
                        <>
                          <td className={s.csvColumnCell}>
                            <div className={s.csvColumnName}>
                              {line.column.header}
                            </div>
                            {line.column.legend && (
                              <div className={s.legend}>
                                {line.column.legend}
                              </div>
                            )}
                          </td>
                          <td className={s.valueCell}>
                            {renderValue(
                              i,
                              line.column,
                              display[line.column.header] ?? "",
                              line.reasons,
                              reasonsId
                            )}
                            {(() => {
                              const fieldPath =
                                Object.prototype.hasOwnProperty.call(
                                  CSV_HEADER_TO_FIELD_PATH,
                                  line.column.header
                                )
                                  ? CSV_HEADER_TO_FIELD_PATH[line.column.header]
                                  : undefined;
                              const normalised = fieldPath
                                ? normalisationsByCell.get(
                                    cellErrorKey(i, fieldPath)
                                  )
                                : undefined;
                              return normalised !== undefined ? (
                                <div className={s.normalisedHint}>
                                  Normalised: {normalised}
                                </div>
                              ) : null;
                            })()}
                          </td>
                        </>
                      ) : (
                        <td className={s.valueCell} colSpan={2}>
                          —
                        </td>
                      )}
                      {showChecks && (
                        <td className={s.checkCell}>
                          <CheckBadge
                            reasons={line.reasons}
                            visibleReasons={visibleReasons}
                            reasonsId={reasonsId}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
